import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  ActivityAgentProcessingStatusDto,
  ActivityDto,
  ActivityWithAuditDto,
  ActivityAutofill,
  AccountActivitySparklineDto,
  PastMeetingActivityDto,
  UpcomingMeetingActivityDto,
} from "@/types";
import { accountKeys, contactKeys } from "@/hooks/queries";
import { ActivityFormData } from "@/components/forms/create-activity-form";
import { getErrorMessage, showErrorToast } from "@/lib/error-handling";
import {
  createActivity,
  fetchActivitiesByAccountId,
  fetchActivitiesByContactId,
  fetchActivityAgentProcessingStatus,
  fetchUpcomingActivitiesByAccountId,
  fetchPastActivitiesByAccountId,
  fetchAccountActivitySparklines,
  updateActivity,
  UpdateActivityRequest,
  activityAutofill,
  deleteActivity,
} from "@/services/frontendServices/activitiesServices";

// Keys for queries, to maintain consistency
export const activityKeys = {
  all: ["activities"] as const,
  lists: () => [...activityKeys.all, "list"] as const,
  list: (filters: { accountId?: string; contactId?: string }) =>
    [...activityKeys.lists(), filters] as const,
  details: () => [...activityKeys.all, "detail"] as const,
  detail: (id: string) => [...activityKeys.details(), id] as const,
  agentProcessingStatus: (id: string) =>
    [...activityKeys.detail(id), "agent-processing-status"] as const,
};

const invalidateRelatedObjectCaches = (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  const relatedObjectKeys = [
    "signalRelatedObjects",
    "goalRelatedObjects",
    "goalProgress",
    "actionItemRelatedObjects",
    "actionItemProgress",
    "technologyRelatedObjects",
  ];

  relatedObjectKeys.forEach((key) => {
    queryClient.invalidateQueries({
      queryKey: [key],
    });
  });
};

// Function overloads for precise typing
async function fetchActivities(filters: {
  accountId: string;
}): Promise<ActivityWithAuditDto[]>;
async function fetchActivities(filters: {
  contactId: string;
}): Promise<ActivityDto[]>;
async function fetchActivities(filters: {
  accountId?: string;
  contactId?: string;
}): Promise<ActivityWithAuditDto[] | ActivityDto[]> {
  if (filters.contactId) {
    return fetchActivitiesByContactId(filters.contactId);
  }

  if (filters.accountId) {
    return fetchActivitiesByAccountId(filters.accountId);
  }

  throw new Error("Either accountId or contactId must be provided");
}

// Hook overloads for precise typing on the return
export function useActivities(filters: {
  accountId: string;
}): ReturnType<typeof useQuery<ActivityWithAuditDto[], Error>>;
export function useActivities(filters: {
  contactId: string;
}): ReturnType<typeof useQuery<ActivityDto[], Error>>;
export function useActivities(filters: {
  accountId?: string;
  contactId?: string;
}) {
  return useQuery({
    queryKey: activityKeys.list(filters),
    queryFn: () => fetchActivities(filters as never),
    enabled: !!(filters.accountId || filters.contactId),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

// Hooks specific for clarity and convenience
export const useAccountActivitySparklines = () => {
  return useQuery<AccountActivitySparklineDto[], Error>({
    queryKey: [...activityKeys.all, "sparklines"],
    queryFn: fetchAccountActivitySparklines,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};

export const useActivitiesByAccount = (accountId: string) => {
  return useActivities({ accountId });
};

export const useActivitiesByContact = (contactId: string) => {
  return useActivities({ contactId });
};

/**
 * Hook to fetch upcoming activities for an account (status = UPCOMING).
 * Used by the "Upcoming Events" widget on the account brief page.
 */
export const useUpcomingActivities = (accountId: string) => {
  return useQuery<UpcomingMeetingActivityDto[], Error>({
    queryKey: [...activityKeys.list({ accountId }), "upcoming"],
    queryFn: () => fetchUpcomingActivitiesByAccountId(accountId),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch the account's past calendar meetings.
 * Used by the "Past Meetings" widget on the account brief page. Keyed under
 * the account's activity list so one invalidation reaches Upcoming and Past.
 */
export const usePastActivities = (accountId: string) => {
  return useQuery<PastMeetingActivityDto[], Error>({
    queryKey: [...activityKeys.list({ accountId }), "past"],
    queryFn: () => fetchPastActivitiesByAccountId(accountId),
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
};

export const useActivityAgentProcessingStatus = (activityId: string) => {
  return useQuery<ActivityAgentProcessingStatusDto, Error>({
    queryKey: activityKeys.agentProcessingStatus(activityId),
    queryFn: () => fetchActivityAgentProcessingStatus(activityId),
    enabled: false,
    retry: 0,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
};

// Hook to create new activity
export const useCreateActivity = () => {
  const queryClient = useQueryClient();

  return useMutation<ActivityDto, Error, ActivityFormData>({
    mutationFn: (formData: ActivityFormData) => createActivity(formData),

    onSuccess: (newActivity, variables) => {
      const accountId = variables.accountId;
      if (accountId) {
        // Update account activities (ActivityWithAuditDto[])
        queryClient.setQueryData<ActivityWithAuditDto[]>(
          activityKeys.list({ accountId }),
          (oldData = []) => {
            return [newActivity as ActivityWithAuditDto, ...oldData];
          }
        );

        // Update activity contacts if contactIds are provided (ActivityDto[])
        if (variables.contactIds?.length) {
          variables.contactIds.forEach((contactId) => {
            queryClient.setQueryData<ActivityDto[]>(
              activityKeys.list({ contactId }),
              (oldData = []) => {
                return [newActivity, ...oldData];
              }
            );
            queryClient.invalidateQueries({
              queryKey: activityKeys.list({ contactId }),
            });
          });
        }

        // Invalidates the queries to force a refetch
        queryClient.invalidateQueries({
          queryKey: activityKeys.list({ accountId }),
        });

        // Invalidate account cache to update last updated date
        queryClient.invalidateQueries({
          queryKey: accountKeys.detail(accountId),
        });

        // IMPORTANT: Invalidate contacts cache when contacts are assigned to activities
        // This ensures the Key Contacts list updates when contact activity changes
        if (variables.contactIds?.length) {
          // Invalidate the account's contacts list
          queryClient.invalidateQueries({
            queryKey: contactKeys.list(accountId),
          });

          // Invalidate the top active contacts cache
          queryClient.invalidateQueries({
            queryKey: [...contactKeys.list(accountId), "topActive"],
          });
        }

        toast.success("Activity created successfully!", {
          description: `Activity has been created.`,
          duration: 3000,
        });
      }
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error creating activity",
        defaultMessage: "Failed to create activity",
        duration: 5000,
      });
    },
  });
};

// Hook to update existing activity
export const useUpdateActivity = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ActivityWithAuditDto,
    Error,
    { activityId: string; formData: UpdateActivityRequest }
  >({
    mutationFn: ({ activityId, formData }) =>
      updateActivity(activityId, formData),
    onSuccess: (updatedActivity, variables) => {
      const { formData } = variables;

      // Update the specific account's activity list
      const accountId = updatedActivity.account?.id;
      if (accountId) {
        queryClient.setQueryData<ActivityWithAuditDto[]>(
          activityKeys.list({ accountId }),
          (oldData) => {
            if (!oldData) return [updatedActivity];
            return oldData.map((activity) =>
              activity.id === updatedActivity.id ? updatedActivity : activity
            );
          }
        );
      }

      // Update the individual activity detail
      queryClient.setQueryData<ActivityWithAuditDto>(
        activityKeys.detail(updatedActivity.id),
        updatedActivity
      );

      // Invalidates the account's activities list query to force a refetch
      if (accountId) {
        queryClient.invalidateQueries({
          queryKey: activityKeys.list({ accountId }),
        });
      }

      // Update activity contacts if contactIds are provided
      if (formData.contactIds?.length) {
        formData.contactIds.forEach((contactId) => {
          queryClient.setQueryData<ActivityDto[]>(
            activityKeys.list({ contactId }),
            (oldData) => {
              if (!oldData) return [updatedActivity];
              return oldData.map((activity) =>
                activity.id === updatedActivity.id ? updatedActivity : activity
              );
            }
          );
          queryClient.invalidateQueries({
            queryKey: activityKeys.list({ contactId }),
          });
        });
      }

      // IMPORTANT: Invalidate contacts cache when contacts are assigned to activities
      // This ensures the Key Contacts list updates when contact activity changes
      if (formData.contactIds?.length) {
        // Invalidate the account's contacts list
        queryClient.invalidateQueries({
          queryKey: contactKeys.list(accountId),
        });

        // Invalidate the top active contacts cache
        queryClient.invalidateQueries({
          queryKey: [...contactKeys.list(accountId), "topActive"],
        });
      }

      // Invalidate all related object caches
      invalidateRelatedObjectCaches(queryClient);

      // Show specific messages for archive/unarchive operations
      if (formData.archived !== undefined) {
        const action = formData.archived ? "archived" : "unarchived";
        toast.success(`Activity ${action} successfully!`, {
          description: `Activity has been ${action}.`,
          duration: 3000,
        });
      } else {
        toast.success("Activity updated successfully!", {
          description: `Activity "${updatedActivity.title}" has been updated.`,
          duration: 3000,
        });
      }
    },
    onError: (error, variables) => {
      const errorMessage = getErrorMessage(error, "Failed to update activity");
      const { formData } = variables;

      // Show specific error messages for archive/unarchive operations
      if (formData.archived !== undefined) {
        const action = formData.archived ? "archive" : "unarchive";
        toast.error(`Failed to ${action} activity`, {
          description: errorMessage,
          duration: 5000,
        });
      } else {
        toast.error("Error updating activity", {
          description: errorMessage,
          duration: 5000,
        });
      }
    },
  });
};

// Hook to fetch a single activity by ID
export function useActivity(activityId: string) {
  return useQuery({
    queryKey: activityKeys.detail(activityId),
    queryFn: async () => {
      // For now, we'll need to fetch from the account activities and find the one we need
      // This is a temporary solution until we have a dedicated API endpoint
      const response = await fetch(`/api/activities/${activityId}`);
      if (!response.ok) {
        throw new Error("Failed to fetch activity");
      }
      return response.json() as Promise<ActivityWithAuditDto>;
    },
    enabled: !!activityId,
  });
}

// Hook to autofill new activity
export function useActivityAutofill() {
  return useMutation<
    ActivityAutofill,
    Error,
    { content: string; accountName: string }
  >({
    mutationFn: async ({ content, accountName }) => {
      if (!content.trim()) {
        throw new Error("Content cannot be empty");
      }
      if (!accountName.trim()) {
        throw new Error("Account context is not ready");
      }
      return await activityAutofill({ content, accountName });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error creating activity",
        defaultMessage: "Failed to create activity",
        duration: 5000,
      });
    },
    onSuccess: (data) => {
      if (!data) return;

      toast.success("AI auto-fill complete!", {
        description: "Fields extracted from your content.",
        duration: 2500,
      });
    },
  });
}

export const useDeleteActivity = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { activityId: string }>({
    mutationFn: async ({ activityId }) => {
      await deleteActivity(activityId);
    },
    onSuccess: () => {
      // Invalidate all activity lists to ensure the deleted activity is removed
      queryClient.invalidateQueries({
        queryKey: activityKeys.lists(),
      });

      // Invalidate all related object caches
      invalidateRelatedObjectCaches(queryClient);

      toast.success("Activity deleted successfully!", {
        description: `Activity has been deleted.`,
        duration: 3000,
      });
    },
    onError: (error) => {
      showErrorToast(error, {
        title: "Error deleting activity",
        defaultMessage: "Failed to delete activity",
        duration: 5000,
      });
    },
  });
};
